import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { getStockMovements, updateStockItem, adjustStock, canWriteInventory, MOVEMENT_TYPES, STOCK_STATUSES } from "../lib/inventory";

const statusOf = (v) => STOCK_STATUSES.find((s) => s.value === v) || { label: v, color: C.textDim };
export function StockStatusBadge({ status }) {
  const s = statusOf(status);
  return <span style={S.pill(s.color)}>{s.label}</span>;
}

const n3 = (x) => (Math.round((Number(x) || 0) * 1000) / 1000).toLocaleString();
const money = (x) => (x == null ? "—" : `€${(Number(x) || 0).toFixed(2)}`);

function Metric({ label, value, color }) {
  return (
    <div style={{ background: C.surface2, borderRadius: 8, padding: "10px 14px", minWidth: 92 }}>
      <div style={{ fontSize: 11, color: C.textDim, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: color || C.text }}>{value}</div>
    </div>
  );
}

/** Stock item detail: live levels, editable reorder/location, manual adjustment, ledger. */
export default function StockItemDetail({ item, onClose, onChanged }) {
  const [moves, setMoves] = useState([]);
  const [loadingMoves, setLoadingMoves] = useState(true);
  const [reorder, setReorder] = useState(item.reorder_level ?? 0);
  const [location, setLocation] = useState(item.warehouse_location || "");
  const [adjQty, setAdjQty] = useState("");
  const [adjReason, setAdjReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // P4-CC15b-2b: updateStockItem() is owner/admin-only. Default FALSE — fail closed
  // — so the Save control (and the editable fields it belongs to) never appear until
  // the database has confirmed this user may use them. An RPC error is treated the
  // same as "not permitted".
  const [canWrite, setCanWrite] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => { try { const ok = await canWriteInventory(); if (alive) setCanWrite(ok); } catch { if (alive) setCanWrite(false); } })();
    return () => { alive = false; };
  }, []);

  const loadMoves = useCallback(async () => {
    setLoadingMoves(true);
    try { const { data } = await getStockMovements({ productId: item.product_id, pageSize: 100 }); setMoves(data); }
    catch (e) { setError(e.message); }
    finally { setLoadingMoves(false); }
  }, [item.product_id]);
  useEffect(() => { loadMoves(); }, [loadMoves]);

  const saveSettings = async () => {
    setBusy(true); setError("");
    try { await updateStockItem(item.id, { reorder_level: Number(reorder) || 0, warehouse_location: location }); onChanged && onChanged(); }
    catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const applyAdjustment = async () => {
    const q = Number(adjQty);
    if (!q) { setError("Enter a non-zero quantity (use a minus sign to remove stock)."); return; }
    setBusy(true); setError("");
    try {
      await adjustStock({ product_id: item.product_id, quantity: q, reason: adjReason || "Manual adjustment" });
      setAdjQty(""); setAdjReason("");
      await loadMoves(); onChanged && onChanged();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 860 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{item.name || "Unnamed product"}</h2>
            <div style={{ color: C.textMuted, fontSize: 13, marginTop: 3, fontFamily: "monospace" }}>{item.sku || "—"}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <StockStatusBadge status={item.stock_status} />
            <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <Metric label="Current" value={n3(item.current_stock)} />
          <Metric label="Available" value={n3(item.available_stock)} color={C.success} />
          <Metric label="Reserved" value={n3(item.reserved_stock)} color={item.reserved_stock > 0 ? C.warning : C.text} />
          <Metric label="Reorder Level" value={n3(item.reorder_level)} />
          <Metric label="Avg Cost" value={money(item.average_cost)} />
          <Metric label="Last Cost" value={money(item.last_purchase_cost)} />
        </div>
        {(item.last_batch_number || item.last_expiry_date) && (
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
            Latest batch: <strong style={{ color: C.text }}>{item.last_batch_number || "—"}</strong>
            {item.last_expiry_date && <> · expires <strong style={{ color: C.text }}>{item.last_expiry_date}</strong></>}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
          {/* Settings */}
          <div style={{ background: C.surface2, borderRadius: 8, padding: 14 }}>
            <div style={{ ...S.sectionTitle, marginTop: 0 }}>Settings</div>
            {canWrite ? (
              <>
                <label style={S.label}>Reorder level</label>
                <input style={S.input} type="number" value={reorder} onChange={(e) => setReorder(e.target.value)} />
                <label style={{ ...S.label, marginTop: 8 }}>Warehouse location</label>
                <input style={S.input} value={location} placeholder="e.g. Aisle 3 / Bay B" onChange={(e) => setLocation(e.target.value)} />
                <button style={{ ...S.btn(), marginTop: 10, opacity: busy ? 0.6 : 1 }} onClick={saveSettings} disabled={busy}>Save settings</button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, color: C.textDim, marginBottom: 8 }}>Read-only — changing reorder level or location requires owner or admin.</div>
                <label style={S.label}>Reorder level</label>
                <div style={{ ...S.input, background: "transparent", color: C.textMuted }}>{n3(item.reorder_level)}</div>
                <label style={{ ...S.label, marginTop: 8 }}>Warehouse location</label>
                <div style={{ ...S.input, background: "transparent", color: C.textMuted }}>{item.warehouse_location || "—"}</div>
              </>
            )}
          </div>
          {/* Manual adjustment — adjustStock() is the shrinkage control CC15b-2b exists
              to restrict to owner/admin; same canWrite gate as Settings, same reasoning:
              no editable-looking inputs left behind with the button gone. */}
          <div style={{ background: C.surface2, borderRadius: 8, padding: 14 }}>
            <div style={{ ...S.sectionTitle, marginTop: 0 }}>Manual adjustment</div>
            {canWrite ? (
              <>
                <label style={S.label}>Quantity change (+ add / − remove)</label>
                <input style={S.input} type="number" value={adjQty} placeholder="e.g. -2" onChange={(e) => setAdjQty(e.target.value)} />
                <label style={{ ...S.label, marginTop: 8 }}>Reason</label>
                <input style={S.input} value={adjReason} placeholder="Stock count / breakage…" onChange={(e) => setAdjReason(e.target.value)} />
                <button style={{ ...S.btn("secondary"), marginTop: 10, opacity: busy ? 0.6 : 1 }} onClick={applyAdjustment} disabled={busy}>Apply adjustment</button>
              </>
            ) : (
              <div style={{ fontSize: 12, color: C.textDim }}>Read-only — adjusting stock levels requires owner or admin.</div>
            )}
          </div>
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 12 }} role="alert">{error}</div>}

        <div style={S.sectionTitle}>Stock movements</div>
        <div style={{ maxHeight: 240, overflowY: "auto" }}>
          <table style={S.table}>
            <thead><tr>{["Date", "Type", "Qty", "Unit Cost", "Batch", "Expiry", "Reference"].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
            <tbody>
              {loadingMoves ? (
                <tr><td colSpan={7} style={S.empty}>Loading…</td></tr>
              ) : moves.length === 0 ? (
                <tr><td colSpan={7} style={S.empty}>No movements yet.</td></tr>
              ) : moves.map((m) => {
                const t = MOVEMENT_TYPES[m.movement_type] || { label: m.movement_type, color: C.textDim };
                const up = Number(m.quantity) >= 0;
                return (
                  <tr key={m.id}>
                    <td style={S.td}>{(m.created_at || "").slice(0, 10)}</td>
                    <td style={S.td}><span style={S.pill(t.color)}>{t.label}</span></td>
                    <td style={{ ...S.td, color: up ? C.success : C.danger, fontWeight: 700 }}>{up ? "+" : ""}{n3(m.quantity)}</td>
                    <td style={S.td}>{money(m.unit_cost)}</td>
                    <td style={S.td}>{m.batch_number || "—"}</td>
                    <td style={S.td}>{m.expiry_date || "—"}</td>
                    <td style={S.td}><span style={{ fontFamily: "monospace", fontSize: 12 }}>{m.reference || "—"}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
